import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';

// Layout
import AppShell from './components/Layout/AppShell';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import ResetPassword from './pages/ResetPassword';
import AdminDashboard from './pages/AdminDashboard';
import VerifierDashboard from './pages/VerifierDashboard';
import Employees from './pages/Employees';
import SalaryManagement from './pages/SalaryManagement';
import LeaveManagement from './pages/LeaveManagement';
import Reports from './pages/Reports';
import AuditLogs from './pages/AuditLogs';
import EmployeeDashboard from './pages/EmployeeDashboard';
import MyPayslips from './pages/MyPayslips';
import MyLeaves from './pages/MyLeaves';
// New Phase 1 & 2 Pages
import Attendance from './pages/Attendance';
import Claims from './pages/Claims';
import TaxDeclarations from './pages/TaxDeclarations';
import Analytics from './pages/Analytics';
// New Phase 3 & 4 Pages
import LeaveEnhancements from './pages/LeaveEnhancements';
// Help Desk
import HelpDesk from './pages/HelpDesk';
// Enterprise Employee Master (360°)
import EmployeeMasterList from './pages/EmployeeMasterList';
import EmployeeMasterView from './pages/EmployeeMasterView';
import EmployeeMasterForm from './pages/EmployeeMasterForm';
import EmployeeMasterImport from './pages/EmployeeMasterImport';
// Rule Configuration
import RuleConfiguration from './pages/RuleConfiguration';
// User Profile
import MyProfile from './pages/MyProfile';
// Promotion & Transfer
import PromotionTransfer from './pages/PromotionTransfer';
// Insurance Module
import InsuranceManagement from './pages/InsuranceManagement';
import MyInsurance from './pages/MyInsurance';
// Advanced Payroll Engine
import PayrollEngine from './pages/PayrollEngine';
// SOP Documents
import SOPDocuments from './pages/SOPDocuments';

import './App.css';

// Role constants for protected routes - prevents inline array re-creation
const ADMIN_HR_ROLES = ['admin', 'hr'];
const VERIFIER_ROLES = ['verifier'];
const ADMIN_HR_VERIFIER_ROLES = ['admin', 'hr', 'verifier'];
const EMPLOYEE_ROLES = ['employee'];
const ALL_ROLES = ['admin', 'hr', 'verifier', 'employee'];

// Protected Route wrapper
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    // Redirect based on role
    if (user?.role === 'employee') return <Navigate to="/employee-dashboard" replace />;
    if (user?.role === 'verifier') return <Navigate to="/verifier-dashboard" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Home redirect based on role
const HomeRedirect = () => {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role === 'employee') return <Navigate to="/employee-dashboard" replace />;
  if (user?.role === 'verifier') return <Navigate to="/verifier-dashboard" replace />;
  return <Navigate to="/dashboard" replace />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ResetPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Home Redirect */}
          <Route path="/" element={<HomeRedirect />} />

          {/* Protected Routes */}
          <Route element={<AppShell />}>
            {/* Admin/HR Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute allowedRoles={ADMIN_HR_ROLES}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employees"
              element={<Navigate to="/employee-master" replace />}
            />
            
            {/* Verifier Dashboard */}
            <Route
              path="/verifier-dashboard"
              element={
                <ProtectedRoute allowedRoles={VERIFIER_ROLES}>
                  <VerifierDashboard />
                </ProtectedRoute>
              }
            />
            
            {/* Shared Routes (Admin/HR/Verifier) */}
            <Route
              path="/salary"
              element={
                <ProtectedRoute allowedRoles={ADMIN_HR_VERIFIER_ROLES}>
                  <SalaryManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/leave-management"
              element={
                <ProtectedRoute allowedRoles={ADMIN_HR_VERIFIER_ROLES}>
                  <LeaveManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <ProtectedRoute allowedRoles={ADMIN_HR_VERIFIER_ROLES}>
                  <Reports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/audit-logs"
              element={
                <ProtectedRoute allowedRoles={ADMIN_HR_VERIFIER_ROLES}>
                  <AuditLogs />
                </ProtectedRoute>
              }
            />
            
            {/* New Phase 1 & 2 Routes */}
            <Route
              path="/attendance"
              element={
                <ProtectedRoute allowedRoles={ALL_ROLES}>
                  <Attendance />
                </ProtectedRoute>
              }
            />
            <Route
              path="/claims"
              element={
                <ProtectedRoute allowedRoles={ALL_ROLES}>
                  <Claims />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tax-declarations"
              element={
                <ProtectedRoute allowedRoles={ALL_ROLES}>
                  <TaxDeclarations />
                </ProtectedRoute>
              }
            />
            <Route
              path="/promotion-transfer"
              element={
                <ProtectedRoute allowedRoles={ALL_ROLES}>
                  <PromotionTransfer />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <ProtectedRoute allowedRoles={ADMIN_HR_VERIFIER_ROLES}>
                  <Analytics />
                </ProtectedRoute>
              }
            />
            
            {/* New Phase 3 & 4 Routes */}
            <Route
              path="/leave-enhancements"
              element={
                <ProtectedRoute allowedRoles={ALL_ROLES}>
                  <LeaveEnhancements />
                </ProtectedRoute>
              }
            />
            
            {/* Advanced Payroll Engine */}
            <Route
              path="/payroll-engine"
              element={
                <ProtectedRoute allowedRoles={ADMIN_HR_VERIFIER_ROLES}>
                  <PayrollEngine />
                </ProtectedRoute>
              }
            />
            
            {/* Help Desk - All Roles */}
            <Route
              path="/help-desk"
              element={
                <ProtectedRoute allowedRoles={ALL_ROLES}>
                  <HelpDesk />
                </ProtectedRoute>
              }
            />

            {/* SOP Documents - Admin/HR only */}
            <Route
              path="/sop-documents"
              element={
                <ProtectedRoute allowedRoles={ADMIN_HR_ROLES}>
                  <SOPDocuments />
                </ProtectedRoute>
              }
            />

            {/* Enterprise Employee Master (360°) Routes */}
            <Route
              path="/employee-master"
              element={
                <ProtectedRoute allowedRoles={ADMIN_HR_VERIFIER_ROLES}>
                  <EmployeeMasterList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee-master/new"
              element={
                <ProtectedRoute allowedRoles={ADMIN_HR_VERIFIER_ROLES}>
                  <EmployeeMasterForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee-master/import"
              element={
                <ProtectedRoute allowedRoles={ADMIN_HR_VERIFIER_ROLES}>
                  <EmployeeMasterImport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee-master/:id"
              element={
                <ProtectedRoute allowedRoles={ADMIN_HR_VERIFIER_ROLES}>
                  <EmployeeMasterView />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee-master/:id/edit"
              element={
                <ProtectedRoute allowedRoles={ADMIN_HR_VERIFIER_ROLES}>
                  <EmployeeMasterForm />
                </ProtectedRoute>
              }
            />

            {/* Rule Configuration (Admin/HR/Verifier Only) */}
            <Route
              path="/rule-configuration"
              element={
                <ProtectedRoute allowedRoles={ADMIN_HR_VERIFIER_ROLES}>
                  <RuleConfiguration />
                </ProtectedRoute>
              }
            />

            {/* Insurance Module Routes */}
            <Route
              path="/insurance"
              element={
                <ProtectedRoute allowedRoles={ADMIN_HR_VERIFIER_ROLES}>
                  <InsuranceManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-insurance"
              element={
                <ProtectedRoute allowedRoles={ALL_ROLES}>
                  <MyInsurance />
                </ProtectedRoute>
              }
            />

            {/* My Profile (All authenticated users) */}
            <Route
              path="/my-profile"
              element={
                <ProtectedRoute allowedRoles={ALL_ROLES}>
                  <MyProfile />
                </ProtectedRoute>
              }
            />

            {/* Employee Routes */}
            <Route
              path="/employee-dashboard"
              element={
                <ProtectedRoute allowedRoles={EMPLOYEE_ROLES}>
                  <EmployeeDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-payslips"
              element={
                <ProtectedRoute allowedRoles={EMPLOYEE_ROLES}>
                  <MyPayslips />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-leaves"
              element={
                <ProtectedRoute allowedRoles={EMPLOYEE_ROLES}>
                  <MyLeaves />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
